/**
 * Tracks the active thread + canvas the chat panel should bind to, plus a
 * `chatHeaderSlot` the canvas route can fill (e.g. ConversationSwitcher).
 *
 * Routes call `setActiveThread(...)` when they mount and pass nulls to
 * clear on unmount. The shell-level `<ChatPanel/>` reads it.
 */

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

interface ActiveThread {
  threadId: string | null;
  canvasId: string | null;
  workspaceId: string | null;
  /**
   * True when the route is in "draft" mode — the user has expressed
   * intent to start a new conversation but no thread row exists yet.
   * The chat panel uses this to show an empty UI with an active
   * composer; the first sent message creates the thread.
   */
  draft?: boolean;
  /**
   * Called by the chat panel after it lazily creates a thread out of
   * a draft (typically: derived a title from the first message, posted
   * the message). The route updates the URL to the new thread id.
   */
  onDraftPromoted?: (threadId: string) => void;
  /**
   * Open a new, empty conversation — the route's "Start a new conversation"
   * (the canvas route's `?conversation=draft`). The chat panel uses it to give
   * a demo's recorded replay a visible conversation of its own. Routes with no
   * such thing leave it unset.
   */
  onStartDraft?: () => void;
}

interface ActiveThreadContextValue extends ActiveThread {
  setActiveThread: (thread: ActiveThread) => void;
  /** Optional slot rendered in the chat header (e.g. ConversationSwitcher). */
  chatHeaderSlot: ReactNode;
  setChatHeaderSlot: (slot: ReactNode) => void;
}

const ActiveThreadContext = createContext<ActiveThreadContextValue | null>(null);

export function ActiveThreadProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveThread>({
    threadId: null,
    canvasId: null,
    workspaceId: null,
    draft: false,
  });
  const [chatHeaderSlot, setChatHeaderSlot] = useState<ReactNode>(null);
  const setActiveThread = useCallback((next: ActiveThread) => setActive(next), []);
  const value = useMemo(
    () => ({ ...active, setActiveThread, chatHeaderSlot, setChatHeaderSlot }),
    [active, setActiveThread, chatHeaderSlot],
  );
  return <ActiveThreadContext.Provider value={value}>{children}</ActiveThreadContext.Provider>;
}

export function useActiveThread(): ActiveThreadContextValue {
  const ctx = useContext(ActiveThreadContext);
  if (!ctx) throw new Error("useActiveThread must be used inside <ActiveThreadProvider>");
  return ctx;
}

/** Convenience hook for routes that own a thread+canvas. Clears on unmount. */
export function useBindActiveThread(
  threadId: string | null,
  canvasId: string | null = null,
  workspaceId: string | null = null,
) {
  const { setActiveThread } = useActiveThread();
  useEffect(() => {
    setActiveThread({ threadId, canvasId, workspaceId });
    return () => setActiveThread({ threadId: null, canvasId: null, workspaceId: null });
  }, [threadId, canvasId, workspaceId, setActiveThread]);
}

/**
 * Convenience hook to render a node in the chat header. Clears on unmount.
 *
 * ⚠️ Callers SHOULD memoize `slot` (e.g. with `useMemo`) so the same JSX
 * object is reused across renders when its content hasn't changed. The
 * hook guards against an infinite loop when `slot` identity churns by
 * comparing against the last-published value via a ref before calling
 * `setChatHeaderSlot`, but an unmemoized slot will still cause the chat
 * header to re-render on every parent commit.
 *
 * The original implementation kept `slot` in the `useEffect` dep array,
 * which caused this exact loop:
 *   render → fresh JSX → effect runs → setState → context value changes →
 *   caller re-renders → fresh JSX → effect runs → … (Maximum update depth
 *   exceeded, surfaced loudest while toggling the chat panel maximise).
 */
export function useChatHeaderSlot(slot: ReactNode) {
  const { setChatHeaderSlot } = useActiveThread();
  const lastPublishedRef = useRef<ReactNode | typeof UNSET>(UNSET);

  useEffect(() => {
    if (lastPublishedRef.current === slot) return;
    lastPublishedRef.current = slot;
    setChatHeaderSlot(slot);
  });

  useEffect(() => {
    return () => {
      lastPublishedRef.current = UNSET;
      setChatHeaderSlot(null);
    };
  }, [setChatHeaderSlot]);
}

const UNSET = Symbol("chai/active-thread/unset");
